import React, { useState, useCallback, useMemo, useEffect } from "react"
import { Button, Tabs, Form, Flex, message } from "antd"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import { updateExisting, skipInvalidData } from "store/clients"
import { routes } from "router"
import ImportSummary from "./components/ImportSummary"
import IndividualFixTab from "./components/IndividualFixTab"
import BulkFixTab from "./components/BulkFixTab"
import DuplicateRecordsSection from "./DuplicateRecordsSection"
import useImportDetail from "./hooks/useImportDetail"
import useRecordEdit from "./hooks/useRecordEdit"
import styles from "./ImportReport.module.css"
import { Modal } from "ui/Modal"
import { toaster } from "components/common/Toast"

const { TabPane } = Tabs

/**
 * ImportReportModal Component
 * Displays detailed import results with options to fix errors
 * Refactored to follow SOLID principles and performance best practices
 */
const ImportReportModal = ({ isModalOpen, onClose }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [form] = Form.useForm()
  const { importClientData, skipInvalidData: skipInvalidState } = useSelector((state) => state.clients)
  const [activeTab, setActiveTab] = useState("1")
  const [fileData, setFileData] = useState(null)
  const [duplicateCount, setDuplicateCount] = useState(0)
  const [duplicateRecords, setDuplicateRecords] = useState({})

  // Use custom hooks
  const importDetail = useImportDetail(importClientData?.data?.import_record?.id)
  const { editingId, handleEditClick, handleSave, handleCancel } = useRecordEdit(importDetail.importId)

  // Update duplicate state when data changes
  useEffect(() => {
    setDuplicateCount(importDetail.duplicateCount)
    setDuplicateRecords(importDetail.duplicateRecords)
  }, [importDetail.duplicateRecords, importDetail.duplicateCount])

  // Auto-close and redirect if no failures or duplicates
  useEffect(() => {
    // Only check after import data has loaded (successCount will be defined and > 0 after successful import)
    if (
      isModalOpen &&
      importDetail.successCount !== undefined &&
      importDetail.successCount > 0 &&
      importDetail.failedCount !== undefined &&
      importDetail.duplicateCount !== undefined
    ) {
      const totalIssues = Number(importDetail.failedCount) + Number(importDetail.duplicateCount)
      if (totalIssues === 0) {
        onClose()
        navigate(routes.guideImportClientHistory())
      }
    }
  }, [isModalOpen, importDetail.successCount, importDetail.failedCount, importDetail.duplicateCount, onClose, navigate])

  /**
   * Handle file change for bulk upload
   * Memoized to prevent unnecessary re-creation
   */
  const handleFileChangeImport = useCallback((file) => {
    setFileData(file)
  }, [])

  /**
   * Handle file upload for bulk fix
   * Memoized to prevent unnecessary re-creation
   */
  const handleFileUpload = useCallback(() => {
    if (!fileData) return

    const formData = new FormData()
    formData.append("file", fileData)
    formData.append("row", importDetail.importId)

    dispatch(updateExisting(formData, importDetail.importId))
      .then((result) => {
        if (result?.message === "File uploaded successfully! File import started!") {
          message.success("File Uploaded Successfully!")
        } else {
          message.error("Something Went Wrong!")
        }
        onClose()
      })
      .catch((error) => {
        console.error("File upload error:", error)
      })
  }, [fileData, importDetail.importId, dispatch, onClose])

  /**
   * Handle skip invalid data
   * Skips all invalid/failed records and closes the modal
   * Redirects to import history page
   */
  const handleSkipInvalid = useCallback(() => {
    if (!importDetail.importId) {
      toaster.error(t("clients.import_report.no_import_id", { defaultValue: "No import ID found" }))
      return
    }

    dispatch(skipInvalidData(importDetail.importId))
      .then((res) => {
        if (res?.message)
          toaster.success(res.message || t("clients.import_report.skip_invalid_success", { defaultValue: "Invalid data skipped successfully" }))
        onClose()
        navigate(routes.guideImportClientHistory())
      })
      .catch((error) => {
        const errorMessage = error?.message || t("clients.import_report.skip_invalid_error", { defaultValue: "Failed to skip invalid data" })
        console.error(errorMessage)
      })
  }, [importDetail.importId, dispatch, onClose, navigate, t])

  /**
   * Handle modal close
   * Memoized to prevent unnecessary re-creation
   */
  const handleModalClose = useCallback(() => {
    onClose()
  }, [onClose])

  /**
   * Handle tab change
   * Memoized to prevent unnecessary re-creation
   */
  const handleTabChange = useCallback((key) => {
    setActiveTab(key)
  }, [])

  /**
   * Handle edit click with form binding
   * Memoized to prevent unnecessary re-creation
   */
  const handleEditWithForm = useCallback(
    (rowNumber, record) => {
      handleEditClick(rowNumber, record, form)
    },
    [handleEditClick, form]
  )

  /**
   * Handle save with form binding
   * Memoized to prevent unnecessary re-creation
   */
  const handleSaveWithForm = useCallback(
    (rowNumber, record) => {
      handleSave(rowNumber, record, form)
    },
    [handleSave, form]
  )

  /**
   * Check if retry button should be disabled
   * Memoized for performance
   */
  const isRetryDisabled = useMemo(() => {
    return activeTab !== "2" || fileData === null
  }, [activeTab, fileData])

  return (
    <Modal
      title={t("clients.import_report.title")}
      open={isModalOpen}
      onCancel={handleModalClose}
      width={800}
      className={styles.importReportModal}
      maskClosable={false}
    >
      {/* Import Summary */}
      <ImportSummary filename={importDetail.filename} successCount={importDetail.successCount} failedCount={importDetail.failedCount} />

      {/* Possible Duplicates */}
      <div className={styles.section}>
        <h3>{t("clients.import_report.possible_duplicates", { count: duplicateCount })}</h3>
        <DuplicateRecordsSection
          duplicateCount={duplicateCount}
          setDuplicateCount={setDuplicateCount}
          duplicateRecords={duplicateRecords}
          setDuplicateRecords={setDuplicateRecords}
          importId={importDetail.importId}
          t={t}
          className={styles.section}
        />
      </div>

      {/* Upload Errors */}
      <div className={styles.section}>
        <h3>{t("clients.import_report.upload_errors", { count: importDetail.failedCount })}</h3>
        <p className={styles.paragraph}>{t("clients.import_report.upload_errors_description")}</p>

        <Tabs activeKey={activeTab} onChange={handleTabChange} className={styles.errorTabs}>
          <TabPane className={styles.individualTabs} tab="Individual Fix" key="1">
            <IndividualFixTab
              failedRecords={importDetail.failedRecords}
              editingId={editingId}
              form={form}
              onEditClick={handleEditWithForm}
              onSave={handleSaveWithForm}
              onCancel={handleCancel}
              isLoading={importClientData?.loading}
            />
          </TabPane>
          <TabPane tab={t("clients.import_report.bulk_fix")} key="2">
            <BulkFixTab failedRecordsUrl={importDetail.failedRecordsUrl} onFileChange={handleFileChangeImport} />
          </TabPane>
        </Tabs>
      </div>

      {/* Footer Buttons */}
      <Flex wrap gap={10} className={styles.footerButtons}>
        <Button onClick={handleSkipInvalid} loading={skipInvalidState?.loading} disabled={skipInvalidState?.loading}>
          {t("clients.import_report.close_skip_invalid")}
        </Button>
        <Button onClick={handleFileUpload} disabled={isRetryDisabled} loading={importClientData?.loading}>
          {t("clients.import_report.retry")}
        </Button>
      </Flex>
    </Modal>
  )
}

ImportReportModal.propTypes = {
  isModalOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired
}

export default ImportReportModal
